import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthContextType, LoginCredentials, User } from '../types';
import { authService } from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track user state changes
  useEffect(() => {
    console.log('👤 USER STATE CHANGED:', user);
    console.log('📦 LocalStorage token:', localStorage.getItem('auth_token') ? 'EXISTS' : 'MISSING');
    console.log('📦 LocalStorage user:', localStorage.getItem('user_data') ? 'EXISTS' : 'MISSING');
    
    // Log permissions for staff members
    if (user && user.role === 'shop_staff') {
      console.log('🔐 Staff Permissions:', user.permissions);
      console.log('🔐 Number of modules:', user.permissions?.length || 0);
    }
  }, [user]);

  // REMOVED: Periodic session validation to prevent logout loops
  // Session is now validated only on initial load and when needed

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');

        if (token && userData) {
          // Use stored user data
          try {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            setIsLoading(false); // Stop loading immediately with cached data

            console.log('Using cached user data:', parsedUser);
          } catch (parseError) {
            console.error('Error parsing stored user data:', parseError);
            localStorage.removeItem('user_data');
            localStorage.removeItem('auth_token');
            setUser(null);
            setIsLoading(false);
          }
        } else {
          // No stored auth data, user needs to login
          setUser(null);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setUser(null);
        setIsLoading(false);
      }
    };

    // Very short timeout as a safety net
    const timeoutId = setTimeout(() => {
      console.warn('Auth initialization timeout, stopping loading');
      setIsLoading(false);
      // Use cached data if available
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    }, 500); // Very short timeout - 500ms

    initAuth().finally(() => {
      clearTimeout(timeoutId);
    });
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      setIsLoading(true);
      console.log('AuthContext: Starting login with', credentials);
      const response = await authService.login(credentials);
      console.log('AuthContext: Login response', response);

      if (response.success && response.data) {
        const userData = response.data.user;
        const token = response.data.token;

        console.log('AuthContext: Setting user and token', { userData, token: token.substring(0, 20) + '...' });
        
        // Log permissions if staff member
        if (userData.role === 'shop_staff') {
          console.log('🔐 Login: Staff permissions received:', userData.permissions);
        }

        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(userData));
        setUser(userData);

        console.log('AuthContext: Login successful, user set to:', userData);
        console.log('AuthContext: User role:', userData.role);

        // Trigger a settings refresh after successful login
        window.dispatchEvent(new CustomEvent('authTokenAvailable'));

        return true;
      } else {
        console.log('AuthContext: Login failed - no success or data', response);
        return false;
      }
    } catch (error: any) {
      console.error('AuthContext: Login error:', error);

      // Re-throw 403 errors (suspended/expired shop) so Login component can handle them
      if (error.response?.status === 403) {
        throw error;
      }

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    console.log('🔴 LOGOUT CALLED - Stack trace:', new Error().stack);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data'); // Also remove user data
    setUser(null);
    authService.logout();
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getMe();
      setUser(currentUser);
      localStorage.setItem('user_data', JSON.stringify(currentUser));

      // Trigger settings refresh to update title if shop name changed
      window.dispatchEvent(new CustomEvent('userDataUpdated'));
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  // Manual token validation - only call when needed
  const validateToken = async () => {
    try {
      // First check if we have local auth data
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');

      if (!token || !userData) {
        console.warn('No auth data found during validation');
        setUser(null);
        return false;
      }

      // For now, just return true if we have local data
      // The API will handle invalid tokens with 401 responses
      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  };

  // Check if user has access to a specific module
  const hasModuleAccess = useCallback((module: string, action: 'view' | 'create' | 'edit' | 'delete' = 'view'): boolean => {
    if (!user) {
      console.log(`🔐 hasModuleAccess(${module}, ${action}): NO USER`);
      return false;
    }

    // Super admins and shop owners have full access
    if (user.role === 'super_admin' || user.role === 'shop_owner') {
      console.log(`🔐 hasModuleAccess(${module}, ${action}): TRUE (${user.role} has full access)`);
      return true;
    }

    // For shop staff, check permissions
    if (!user.permissions || user.permissions.length === 0) {
      console.log(`🔐 hasModuleAccess(${module}, ${action}): FALSE (no permissions array)`);
      return false;
    }

    const permission = user.permissions.find(p => p.module === module);
    if (!permission) {
      console.log(`🔐 hasModuleAccess(${module}, ${action}): FALSE (module not found in permissions)`);
      return false;
    }

    let result = false;
    switch (action) {
      case 'view':
        result = permission.can_view;
        break;
      case 'create':
        result = permission.can_create;
        break;
      case 'edit':
        result = permission.can_edit;
        break;
      case 'delete':
        result = permission.can_delete;
        break;
      default:
        result = false;
    }
    
    console.log(`🔐 hasModuleAccess(${module}, ${action}): ${result}`, { permission });
    return result;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, validateToken, isLoading, hasModuleAccess }}>
      {children}
    </AuthContext.Provider>
  );
};